import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";

import { useAnnounce } from "../app/announcer";
import { useRouter } from "../app/router";
import { useStudio } from "../app/store";
import { PhysicsThread } from "../components/PhysicsThread";
import { SlidePreview, type SlidePalette } from "../components/SlidePreview";

const PALETTES: Array<{ name: string; note: string; palette: SlidePalette }> = [
  { name: "Soft machinery", note: "warm paper · sharp signal", palette: { background: "#f3e6d2", surface: "#dfcbb2", text: "#1d2026", muted: "#6b615b", accent: "#cf3e74", accentSecondary: "#3158bd", onAccent: "#fff9ef" } },
  { name: "Night swimming", note: "deep field · electric interruption", palette: { background: "#161923", surface: "#272c3a", text: "#f4eadc", muted: "#ada6a2", accent: "#f0579a", accentSecondary: "#6f8df5", onAccent: "#11131b" } },
  { name: "Apricot proof", note: "bright room · grown-up warmth", palette: { background: "#f6d8bd", surface: "#e7b997", text: "#2c191c", muted: "#77575c", accent: "#b62f61", accentSecondary: "#324c9e", onAccent: "#fff7ed" } },
  { name: "Quiet voltage", note: "cool air · one loud idea", palette: { background: "#dfe9ea", surface: "#c4d5d8", text: "#142022", muted: "#516467", accent: "#d6427e", accentSecondary: "#284ec5", onAccent: "#fff8ef" } },
];

export const Home = () => {
  const { dispatch } = useStudio();
  const { navigate } = useRouter();
  const announce = useAnnounce();
  const [paletteIndex, setPaletteIndex] = useState(0);
  const artRef = useRef<HTMLDivElement>(null);
  const frame = useRef<number | null>(null);
  const active = PALETTES[paletteIndex] ?? PALETTES[0]!;

  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
  }, []);

  const moveLight = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const element = artRef.current;
    if (!element) return;
    const bounds = element.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      element.style.setProperty("--light-x", `${(x * 100).toFixed(1)}%`);
      element.style.setProperty("--light-y", `${(y * 100).toFixed(1)}%`);
      element.style.setProperty("--art-rx", `${((0.5 - y) * 2.3).toFixed(2)}deg`);
      element.style.setProperty("--art-ry", `${((x - 0.5) * 2.9).toFixed(2)}deg`);
    });
  };

  const start = (path: "quick" | "guided") => {
    dispatch({ type: "enter", entry: "colour", colourPath: path });
    announce(path === "quick" ? "Opening Color, Please. One image, then your palette." : "Opening the guided Color, Please route.");
    navigate("/studio");
  };

  const shuffle = () => setPaletteIndex((current) => (current + 1) % PALETTES.length);

  return (
    <div className="home-shell standalone-home standalone-home--color">
      <section className="hero-v2 product-hero" aria-labelledby="home-title">
        <div className="hero-v2__copy">
          <p className="eyebrow">A PITCH.DOG TOOL FOR FINDING THE LIGHT</p>
          <h1 id="home-title">One image.<br /><i>A whole color world.</i></h1>
          <p className="home-standfirst">Color, Please turns a photograph, still, painting, texture, or stubborn little reference into a palette built for slides. Four essential colors first. The full system when you ask.</p>

          <div className="teaser-v2 color-teaser">
            <div className="palette-teaser-head"><span>Try the feeling</span><strong>{active.name}</strong><small>{active.note}</small></div>
            <div className="palette-teaser-options palette-teaser-options--large" aria-label="Sample color worlds">
              {PALETTES.map((item, index) => (
                <button key={item.name} type="button" aria-label={`Preview ${item.name}`} aria-pressed={paletteIndex === index} onClick={() => setPaletteIndex(index)}>
                  {[item.palette.background, item.palette.text, item.palette.accent, item.palette.accentSecondary].map((hex) => <span key={hex} style={{ background: hex }} />)}
                </button>
              ))}
            </div>
            <div className="hero-actions">
              <button type="button" className="btn btn--primary btn--large" onClick={() => start("quick")}>Use my image <span aria-hidden="true">→</span></button>
              <button type="button" className="btn" onClick={shuffle}>Shuffle the light</button>
              <button type="button" className="text-link" onClick={() => start("guided")}>Take the guided route</button>
            </div>
            <p className="time-note">Your image never leaves the tab. We are curious, not creepy.</p>
          </div>
        </div>

        <div ref={artRef} className="hero-v2__art product-hero__art product-hero__art--color" onPointerMove={moveLight} onPointerLeave={() => {
          artRef.current?.style.setProperty("--light-x", "58%");
          artRef.current?.style.setProperty("--light-y", "38%");
          artRef.current?.style.setProperty("--art-rx", "0deg");
          artRef.current?.style.setProperty("--art-ry", "0deg");
        }}>
          <picture className="optical-master" aria-hidden="true">
            <source srcSet="./assets/letters-light-optical-still-v1-960w.webp 960w, ./assets/letters-light-optical-still-v1-1600w.webp 1600w" type="image/webp" />
            <img src="./assets/letters-light-optical-still-v1.png" alt="" />
          </picture>
          <span className="hero-v2__light" aria-hidden="true" />
          <PhysicsThread variant="color" />
          <div className="hero-v2__slide color-hero-slide">
            <SlidePreview kicker="A COLOR WORLD WITH JOBS" title="Make the room feel it." body="Four essential colors now. The complete working system when the deck asks for it." palette={active.palette} variant="titleBody" label={`${active.name} · live 16:9 preview`} />
          </div>
          <p className="hero-v2__note"><span>PULL THE LIGHT</span><small>A tiny bit of physics. Because color should behave less like a spreadsheet.</small></p>
        </div>
      </section>

      <section className="product-promise" aria-labelledby="promise-title">
        <header>
          <p className="eyebrow">THE IMAGE IS A SOURCE, NOT A COLOR PICKER</p>
          <h2 id="promise-title">Every color gets a job on the slide.</h2>
        </header>
        <div className="promise-grid">
          <article><span>01</span><strong>Bring the atmosphere.</strong><p>Use the whole image by default. Crop and protect exact colors only when they matter.</p></article>
          <article><span>02</span><strong>Choose your distance.</strong><p>Stay faithful, borrow the mood, or push. The engine shows its work either way.</p></article>
          <article><span>03</span><strong>Play without breaking it.</strong><p>Shuffle, save, lock, nudge, inspect contrast, and undo every experiment.</p></article>
        </div>
      </section>

      <section className="color-river" aria-label="Four sample color worlds">
        {PALETTES.map((item, index) => (
          <button key={item.name} type="button" aria-pressed={paletteIndex === index} onClick={() => setPaletteIndex(index)} style={{ "--river-bg": item.palette.background, "--river-ink": item.palette.text, "--river-accent": item.palette.accent, "--river-accent-two": item.palette.accentSecondary } as CSSProperties}>
            <span>{String(index + 1).padStart(2, "0")}</span><strong>{item.name}</strong><small>{item.note}</small><i aria-hidden="true" />
          </button>
        ))}
      </section>

      <section className="manifesto-strip manifesto-strip--color" aria-label="Color Please in one sentence">
        <p><span>Keep the temperature.</span><span>Give every color a job.</span></p>
        <small>A palette is a working system, not five attractive hex codes holding hands on Pinterest.</small>
      </section>

      <section className="closing-invite">
        <p className="eyebrow">THE IMAGE ALREADY KNOWS THE FEELING</p>
        <h2>Let us turn it into something you can use.</h2>
        <button type="button" className="btn btn--primary btn--large" onClick={() => start("quick")}>
          Start Color, Please <span aria-hidden="true">→</span>
        </button>
      </section>
    </div>
  );
};
